import { Point } from 'pixi.js';
import { MonoBehavior } from '../core/monoBehavior';
import { addToLevel } from '../core/Playable2DRootContainer';
import { GameEvent } from '../events/gameEvent';
import { EventName } from '../events/eventsName';
import { Layout } from '../LayoutManager';
import gsap, { Power1 } from 'gsap';
import { CARD_MOVE_TIME } from 'constants/move';
import { Displays, DESKTOP_SIDEBAR_WIDTH, DESKTOP_UI_WIDTH, BUTTONS_BOTTOM_MARGIN_DESKTOP } from 'constants/layout';
import { CircleButton } from './circleButton';
import { Store } from 'game/store/gameStore';
import { MagicUIParticles } from 'game/MagicUIParticles';
import { SoundManager } from 'game/core/soundManager';
import { GAEvent, GAEventNames } from 'game/analytics/events';

export class MagicWandButton extends MonoBehavior {
    settingsOpen = false;
    blockMove = Infinity;
    button: CircleButton;
    disable = true;

    constructor() {
        super();
        this.wandClicked = this.wandClicked.bind(this);
        this.blockButton = this.blockButton.bind(this);
        this.setDisabled = this.setDisabled.bind(this);
        this.onOpenSettings = this.onOpenSettings.bind(this);
        this.onCloseSettings = this.onCloseSettings.bind(this);
        this.updateCounter = this.updateCounter.bind(this);
        this.modalClosed = this.modalClosed.bind(this);

        this.button = new CircleButton('wand', true);
        GameEvent.add(EventName.BLOCK_ACTION_BUTTONS, this.blockButton);
        GameEvent.add(EventName.OPEN_SETTINGS, this.onOpenSettings);
        GameEvent.add(EventName.HIDE_SETTINGS, this.onCloseSettings);
        GameEvent.add(EventName.SET_WAND_DISABLED, this.setDisabled);
        GameEvent.add(EventName.GAME_INTRO_COMPLETED, () => this.onReady());
        GameEvent.add(EventName.ADS_GRANTED, this.updateCounter);
        GameEvent.add(EventName.WAND_SHOW, this.updateCounter);
        GameEvent.add(EventName.CLOSE_MODAL, this.modalClosed);
        GameEvent.add(EventName.NO_MAGIC_KING_INFO, this.onNoMagicKingInfo.bind(this));
        GameEvent.add(EventName.BLOCK_MAGIC_WAND, this.setDisabled);

        GameEvent.add(EventName.ADS_CLOSED, () => {
            this.blockButton(0);
        });
    }
    onNoMagicKingInfo() {
        this.blockButton(Infinity);
    }

    modalClosed({ id, confirm }: { id: string; confirm: boolean }) {
        const gameData = {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        };
        if (id === 'magic-wand' && confirm) {
            this.onboardingConfirm();
        }
        if (id === 'out-wand') {
            if (confirm) {
                GAEvent(GAEventNames.MAGIC_WAND_NO_MOVES_CONFIRM, gameData);
                this.wandClicked();
            } else {
                GAEvent(GAEventNames.MAGIC_WAND_NO_MOVES_CLOSE, gameData);
            }
        }
        if (id === 'out-move-ads') {
            if (confirm) {
                this.wandClicked();
                GAEvent(GAEventNames.OUT_MOVE_AD_CONFIRM, gameData);
            } else {
                GAEvent(GAEventNames.OUT_MOVE_AD_CLOSE, gameData);
            }
        }
        if (id === 'out-move-wand') {
            if (confirm) {
                GAEvent(GAEventNames.OUT_MOVE_WAND_POPUP_CONFIRM, gameData);
                this.wandClicked();
            } else {
                GAEvent(GAEventNames.OUT_MOVE_WAND_POPUP_CLOSE, gameData);
            }
        }
    }
    onboardingConfirm() {
        this.wandClicked();
        GAEvent(GAEventNames.MAGIC_WAND_ONBOARDING_TRY, {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
    }
    blockButton(blockTime: number) {
        this.blockMove = performance.now() + blockTime;
    }
    onReady() {
        this.setDisabled(false);
        this.blockMove = 0;
        this.updateCounter();
    }

    start(): void {
        addToLevel(this.button.container);
        this.button.onClick(this.wandClicked);
        this.button.container.zIndex = 10;
    }
    wandClicked() {
        if (this.blockMove > performance.now()) return;
        SoundManager.play('magicWandClickSFX');
        if (Store.wandCounter < 1) {
            // if (!Store.hasPopup('out-wand')) {
            //     showPopup('out-wand');
            //     GAEvent(GAEventNames.MAGIC_WAND_NO_MOVES, {
            //         id: Store.gameId,
            //         type: Store.getGameTypeText(),
            //         difficulty: Store.getDifficultyText(),
            //     });
            // } else {
            GameEvent.emit(EventName.SHOW_ADS, { place: 'wand' });
            // }
            return;
        }
        this.wand();
    }
    wand() {
        new MagicUIParticles(this.button.container);
        GAEvent(GAEventNames.MAGIC_WAND_CLICK, {
            moves: Store.movesCount,
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
        GameEvent.emit(EventName.MAGIC_WAND);
    }
    updateWandCounter() {
        this.updateCounter();
    }

    setDisabled(disabled: boolean) {
        this.disable = disabled;
        this.button.setDisabled(this.disable);
    }

    resize(width: number, height: number): void {
        const display = Layout.getDisplay();
        const position = new Point();
        const padding = Layout.getButtonPadding();
        const buttonSize = Layout.getButtonSize();
        switch (display) {
            case Displays.MOBILE:
            case Displays.TABLET:
                position.set(width / 2 - buttonSize * 2.5 - padding.x * 3, height / 2 - buttonSize / 2 - padding.y);

                break;
            case Displays.DESKTOP:
                position.set(
                    -DESKTOP_SIDEBAR_WIDTH / 2 - (buttonSize + padding.x),
                    height / 2 - buttonSize * 0.5 - BUTTONS_BOTTOM_MARGIN_DESKTOP
                );
                break;
        }
        this.button.container.position = position;
        this.updateCounter();
    }

    onOpenSettings() {
        const display = Layout.getDisplay();
        const buttonSize = Layout.getButtonSize();
        const padding = Layout.getButtonPadding();

        if (display === Displays.DESKTOP) {
            gsap.to(this.button.container.position, {
                x: -DESKTOP_UI_WIDTH / 2 - (buttonSize + padding.x),
                ease: Power1.easeOut,
                duration: CARD_MOVE_TIME,
            });
        }
    }
    onCloseSettings() {
        const display = Layout.getDisplay();
        const buttonSize = Layout.getButtonSize();
        const padding = Layout.getButtonPadding();

        if (display === Displays.DESKTOP) {
            gsap.to(this.button.container.position, {
                x: -DESKTOP_SIDEBAR_WIDTH / 2 - (buttonSize + padding.x),
                ease: Power1.easeOut,
                duration: CARD_MOVE_TIME,
            });
        }
    }
    updateCounter() {
        const label = Store.wandCounter > 0 ? Store.wandCounter : 'Ad';
        this.button.addLabel(label);
    }
}
